import { useEffect } from 'react';
import type { PublicPlayer, Snapshot } from 'shared';
import { Avatar } from '../design/Avatar';
import { Button } from '../design/Button';
import { Panel } from '../design/Panel';
import { Label } from '../design/Paper';
import { Stamp } from '../design/Stamp';
import { tiltFor } from '../design/tokens';
import { VoteCard } from '../design/VoteCard';
import { ro } from '../i18n/ro';
import { playVoteCue, vibrate } from '../state/cues';
import { voteJustOpened } from '../state/connection';
import { useRoom } from '../state/RoomConnection';

/**
 * A player's phone. Always shows something relevant to the moment; the vote
 * itself is two very large targets and nothing else.
 */
export function PlayerScreen({ snapshot }: { snapshot: Snapshot }) {
  const room = useRoom();
  const me = snapshot.players.find((p) => p.id === snapshot.viewer.playerId) ?? null;

  // Best-effort alert. Haptics are Android-only and audio needs the tab in the
  // foreground, so the projector carries the load.
  const opened = voteJustOpened(room);
  useEffect(() => {
    if (opened) {
      vibrate();
      playVoteCue();
    }
  }, [opened]);

  if (me && !me.alive) return <Executed snapshot={snapshot} />;

  const session = snapshot.session;

  if (session?.status === 'VOTING') {
    const canVote = snapshot.permissions.canVote;
    const myChoice = session.myChoice;

    if (canVote) {
      return (
        <VotingPrompt
          snapshot={snapshot}
          onVote={(choice) => room.send({ type: 'vote', sessionId: session.id, choice })}
          votesCast={session.votesCast}
          eligibleCount={session.eligibleCount}
          voters={session.eligibleVoterIds}
          votedPlayerIds={session.votedPlayerIds}
        />
      );
    }

    return (
      <Waiting
        snapshot={snapshot}
        myChoice={myChoice}
        votesCast={session.votesCast}
        eligibleCount={session.eligibleCount}
        voters={session.eligibleVoterIds}
        votedPlayerIds={session.votedPlayerIds}
        reason={me ? null : ro.cannotVoteAdmin}
      />
    );
  }

  if (session?.status === 'REVEALED') {
    const passed = session.outcome === 'PASSED';
    return (
      <main className="flex min-h-0 flex-1 flex-col items-center px-4 pb-5 pt-6">
        <div data-testid="player-verdict" data-outcome={session.outcome}>
          <Stamp tone={passed ? 'teal' : 'red'} size="md" className={tiltFor('verdict')}>
            {passed ? ro.governmentApproved : ro.governmentRejected}
          </Stamp>
        </div>
        <div className="mt-4 flex items-baseline gap-3 font-display leading-none">
          <span className="text-5xl text-teal">{session.jaCount}</span>
          <span className="text-xl text-ink-faint">—</span>
          <span className="text-5xl text-red">{session.neinCount}</span>
        </div>

        <Panel tone="sunk" press="sm" className="mt-6 w-full" innerClassName="p-3">
          <div className="flex flex-col gap-1">
            {session.votes.map((vote) => (
              <div
                key={vote.playerId}
                className="flex items-center gap-2.5 border-b border-ink/15 py-1.5 last:border-0"
              >
                <Avatar subject={{ ...vote, id: vote.playerId, avatarVersion: 0 }} size="sm" />
                <span className="min-w-0 flex-1 truncate text-sm">{vote.name}</span>
                <VoteCard face={vote.choice} size="sm" />
              </div>
            ))}
          </div>
        </Panel>
      </main>
    );
  }

  return <BetweenVotes snapshot={snapshot} />;
}

function GovernmentPair({ snapshot, ids }: { snapshot: Snapshot; ids: { presidentId: string; chancellorId: string } }) {
  const find = (id: string): PublicPlayer | null => snapshot.players.find((p) => p.id === id) ?? null;
  const seats: Array<[string, PublicPlayer | null]> = [
    [ro.president, find(ids.presidentId)],
    [ro.chancellor, find(ids.chancellorId)],
  ];

  return (
    <div className="grid w-full grid-cols-2 gap-3">
      {seats.map(([role, person]) => (
        <Panel key={role} press="sm" rule innerClassName="p-3 text-center">
          {person ? (
            <Avatar
              subject={person}
              size="lg"
              dimmed={!person.alive}
              className="mx-auto h-28 w-28 text-3xl"
            />
          ) : (
            <div className="mx-auto h-28 w-28 bg-paper-sunk" />
          )}
          <div className="mt-3 truncate font-display text-sm uppercase">{person?.name ?? '—'}</div>
          <Label className="mt-1 block text-[0.55rem] text-ink-faint">{role}</Label>
        </Panel>
      ))}
    </div>
  );
}

function VoterStrip({
  snapshot,
  voters,
  votedPlayerIds,
  votesCast,
  eligibleCount,
}: {
  snapshot: Snapshot;
  voters: string[];
  votedPlayerIds: string[];
  votesCast: number;
  eligibleCount: number;
}) {
  const voted = new Set(votedPlayerIds);
  return (
    <div className="flex flex-col items-center gap-2">
      <Label className="text-[0.6rem] text-ink-soft">{ro.votesCast(votesCast, eligibleCount)}</Label>
      <div className="flex flex-wrap justify-center gap-2">
        {voters.map((id) => {
          const player = snapshot.players.find((p) => p.id === id);
          if (!player) return null;
          return <Avatar key={id} subject={player} size="sm" dimmed={!voted.has(id)} />;
        })}
      </div>
    </div>
  );
}

function VotingPrompt({
  snapshot,
  onVote,
  votesCast,
  eligibleCount,
  voters,
  votedPlayerIds,
}: {
  snapshot: Snapshot;
  onVote: (choice: 'JA' | 'NEIN') => void;
  votesCast: number;
  eligibleCount: number;
  voters: string[];
  votedPlayerIds: string[];
}) {
  const session = snapshot.session;
  if (session?.status !== 'VOTING') return null;

  return (
    <main className="flex min-h-0 flex-1 flex-col px-4 pb-5 pt-5">
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4">
        <Label className="text-center text-[0.65rem] text-ink-soft">{ro.proposedGovernment}</Label>
        <GovernmentPair snapshot={snapshot} ids={session.government} />
        <div className="mt-1">
          <VoterStrip
            snapshot={snapshot}
            voters={voters}
            votedPlayerIds={votedPlayerIds}
            votesCast={votesCast}
            eligibleCount={eligibleCount}
          />
        </div>
      </div>

      <div className="mt-6 grid shrink-0 grid-cols-2 gap-4">
        <Button tone="teal" size="vote" block data-testid="vote-ja" onClick={() => onVote('JA')}>
          {ro.ja}
        </Button>
        <Button tone="red" size="vote" block data-testid="vote-nein" onClick={() => onVote('NEIN')}>
          {ro.nein}
        </Button>
      </div>
    </main>
  );
}

function Waiting({
  snapshot,
  myChoice,
  votesCast,
  eligibleCount,
  voters,
  votedPlayerIds,
  reason,
}: {
  snapshot: Snapshot;
  myChoice: 'JA' | 'NEIN' | null;
  votesCast: number;
  eligibleCount: number;
  voters: string[];
  votedPlayerIds: string[];
  reason: string | null;
}) {
  const session = snapshot.session;

  return (
    <main className="flex min-h-0 flex-1 flex-col px-4 pb-5 pt-6">
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5">
        {myChoice ? (
          <>
            <Label className="text-[0.65rem] text-ink-soft">{ro.yourVote}</Label>
            <div data-testid="my-vote" data-choice={myChoice}>
              <VoteCard face={myChoice} size="md" className="h-40 w-28 text-2xl" />
            </div>
            <Stamp tone="ink" size="sm" className={tiltFor('locked')}>
              {ro.hasVoted}
            </Stamp>
          </>
        ) : (
          <>
            <Label className="text-center text-[0.65rem] text-ink-soft">{ro.cannotVote}</Label>
            {reason && <p className="max-w-64 text-center text-sm text-ink-soft">{reason}</p>}
            {session?.status === 'VOTING' && (
              <GovernmentPair snapshot={snapshot} ids={session.government} />
            )}
          </>
        )}
      </div>

      <Panel tone="sunk" press="sm" className="mt-5 shrink-0" innerClassName="px-3 py-4">
        <Label className="block text-center text-[0.6rem] text-ink-soft">
          {ro.waitingForVotes}
        </Label>
        <div className="mt-3">
          <VoterStrip
            snapshot={snapshot}
            voters={voters}
            votedPlayerIds={votedPlayerIds}
            votesCast={votesCast}
            eligibleCount={eligibleCount}
          />
        </div>
      </Panel>
    </main>
  );
}

function BetweenVotes({ snapshot }: { snapshot: Snapshot }) {
  return (
    <main className="flex min-h-0 flex-1 flex-col items-center px-4 pb-5 pt-6">
      <Label className="text-center text-[0.65rem] text-ink-soft">
        {snapshot.phase === 'LOBBY' ? ro.waitingToStart : ro.noVoting}
      </Label>

      {snapshot.electedGovernment ? (
        <div className="mt-5 w-full">
          <Label className="mb-3 block text-center text-[0.6rem] text-ink-faint">
            {ro.electedGovernment}
          </Label>
          <GovernmentPair snapshot={snapshot} ids={snapshot.electedGovernment} />
        </div>
      ) : (
        <p className="mt-6 text-sm text-ink-faint">{ro.noGovernment}</p>
      )}

      <Panel tone="sunk" press="sm" className="mt-6 w-full" innerClassName="p-3">
        <Label className="block text-[0.6rem] text-ink-soft">
          {ro.players} · {snapshot.players.length}
        </Label>
        <div className="mt-3 flex flex-wrap gap-2">
          {snapshot.players.map((p) => (
            <div key={p.id} className="flex items-center gap-1.5">
              <Avatar subject={p} size="sm" dimmed={!p.alive} />
            </div>
          ))}
        </div>
      </Panel>
    </main>
  );
}

function Executed({ snapshot }: { snapshot: Snapshot }) {
  return (
    <main className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
      <Stamp tone="red" size="md" className={tiltFor('dead')}>
        {ro.executedYou}
      </Stamp>
      <p className="max-w-72 text-sm text-ink-soft">{ro.executedBody}</p>
      {snapshot.session?.status === 'VOTING' && (
        <div className="mt-4 w-full">
          <GovernmentPair snapshot={snapshot} ids={snapshot.session.government} />
        </div>
      )}
    </main>
  );
}
